// src/services/jobsService.ts
// Fetches live jobs from the Cloudflare Worker API (Neon database)
// Maps the API response to the Job shape Jobs.tsx expects.
//
// Field name notes (schema vs frontend):
//   jobs.name          → MappedJob.description   (short job title shown in the UI)
//   jobs.description   → MappedJob.descriptionLong (long scope of works)
//   jobs.endDate       → MappedJob.targetEnd
//   jobs.address + suburb + state → MappedJob.siteAddress (assembled here)

import { api } from '../utils/api'

// What the API returns — Drizzle returns camelCase from the Worker
interface ApiJob {
  id: string
  // Job title — stored as `name` in the DB, shown as `description` in the UI
  name?: string
  // Long-form scope of works — stored as `description` in the DB
  description?: string | null
  description_long?: string | null   // legacy snake_case fallback
  jobNumber?: string
  job_number?: string
  clientId?: string | null
  client_id?: string | null
  // Location fields — assembled into siteAddress
  address?: string | null
  suburb?: string | null
  state?: string | null
  // Presented to the frontend as `siteAddress` (pre-assembled string)
  siteAddress?: string | null
  site_address?: string | null
  status?: string
  // Dates
  startDate?: string | null
  start_date?: string | null
  endDate?: string | null            // DB column — maps to targetEnd in the frontend
  end_date?: string | null           // snake_case fallback
  targetEnd?: string | null          // legacy camelCase (kept for backwards compat)
  target_end?: string | null
  // Financials
  contractValue?: number | string | null
  contract_value?: number | string | null
  budget?: number | string | null
  committedCost?: number | string | null
  committed_cost?: number | string | null
  // References
  contractType?: string | null
  contract_type?: string | null
  contractNumber?: string | null
  contract_number?: string | null
  pmId?: string | null
  pm_id?: string | null
}

// What Jobs.tsx expects — matches typeof MOCK_JOBS[0]
export interface MappedJob {
  id: string
  jobNumber: string
  description: string       // short job title (from jobs.name)
  descriptionLong: string   // long scope of works (from jobs.description)
  type: string
  clientId: string
  startDate: string | undefined
  targetEnd: string | undefined
  contractValue: number
  contractType: string
  siteAddress: string
  status: string
  progressPercent: number
  crew: number
  plantOnSite: number
  openIssues: number
  openHoldPoints: number
  openNCRs: number
  poCommitted: number
  clientName: string
  clientContact: string
  pmId: string
  pmName: string
  contractNumber: string
  bomLines: never[]
}

function mapApiJobToAppJob(raw: ApiJob): MappedJob {
  // Assemble siteAddress from individual location fields (API returns them separately)
  const siteAddressParts = [raw.address, raw.suburb, raw.state].filter(Boolean)
  const siteAddress = siteAddressParts.length > 0
    ? siteAddressParts.join(', ')
    : (raw.siteAddress ?? raw.site_address ?? '')

  return {
    id:             raw.id,
    // jobs.name is the short title displayed throughout the UI as "description"
    description:    raw.name ?? '',
    // jobs.description is the long-form scope of works
    descriptionLong: raw.description ?? raw.description_long ?? '',
    jobNumber:      raw.jobNumber ?? raw.job_number ?? '',
    type:           'SIGNALS',            // type column not yet in schema — default pending migration
    clientId:       raw.clientId ?? raw.client_id ?? 'c1',
    startDate:      raw.startDate ?? raw.start_date ?? undefined,
    // jobs.endDate maps to targetEnd in the frontend
    targetEnd:      raw.endDate ?? raw.end_date ?? raw.targetEnd ?? raw.target_end ?? undefined,
    contractValue:  Number(raw.contractValue ?? raw.contract_value ?? 0),
    contractType:   raw.contractType ?? raw.contract_type ?? 'LUMP_SUM',
    siteAddress,
    status:         raw.status ?? 'NOT_STARTED',
    contractNumber: raw.contractNumber ?? raw.contract_number ?? '',
    pmId:           raw.pmId ?? raw.pm_id ?? '',
    // Computed fields — defaulted until joined from DB in a future step
    progressPercent: 0,
    crew:            0,
    plantOnSite:     0,
    openIssues:      0,
    openHoldPoints:  0,
    openNCRs:        0,
    poCommitted:     Number(raw.committedCost ?? raw.committed_cost ?? 0),
    clientName:      '',   // enriched from clients join — Phase 2 step
    clientContact:   '',
    pmName:          '',   // enriched from employees join — Phase 2 step
    bomLines:        [],
  }
}

export async function fetchJobs(): Promise<MappedJob[]> {
  const { data } = await api.get<{ data: ApiJob[] } | ApiJob[]>('/api/jobs')
  const raw: ApiJob[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiJobToAppJob)
}
